import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type {
  TodoNoteCategoryDraft,
  TodoNoteItemDraft,
  TodoRoutineCompleteMark,
  TodoRoutineConfig,
  TodoRoutineDisplayUnit,
  TodoRoutineFrequency,
} from '../../types/todoNote';
import {
  defaultRoutineConfig,
  getRoutineOccurrenceGapMessage,
  normalizeRoutineDayLogs,
} from '../../utils/todoRoutine';
import {
  CreateDateField,
  IconMinus,
  IconPlus,
  IconReset,
  todayIso,
} from '../assignmentPoints/assignmentCreateModalShared';
import { SortableRow } from './TodoNoteSortableRow';

const WEEKDAY_OPTIONS = [
  { v: 0, label: '일' },
  { v: 1, label: '월' },
  { v: 2, label: '화' },
  { v: 3, label: '수' },
  { v: 4, label: '목' },
  { v: 5, label: '금' },
  { v: 6, label: '토' },
] as const;

export type TodoRoutineCreatePayload = {
  draft: TodoNoteItemDraft;
  routine: TodoRoutineConfig;
};

type Props = {
  open: boolean;
  draft: TodoNoteItemDraft | null;
  categories: TodoNoteCategoryDraft[];
  categoryInput: string;
  saving?: boolean;
  /** 생성(기본) / 설정 수정 */
  mode?: 'create' | 'edit';
  /** edit 시 기존 루틴 설정( dayLogs 포함 ) */
  initialRoutine?: TodoRoutineConfig | null;
  /**
   * 캘린더·포스트잇에서 연 발생일(YYYY-MM-DD).
   * 있으면 완료 체크가 이 날짜 dayLog에 반영된다.
   */
  occurrenceYmd?: string | null;
  /**
   * 완료 체크 표시 조건.
   * - always: 발생일이 있으면 표시(미래 포함)
   * - never: 숨김 (타임테이블 전체 설정 수정 등)
   */
  completeToggleMode?: 'always' | 'never';
  completeMark?: TodoRoutineCompleteMark | null;
  onToggleComplete?: () => void;
  onDelete?: () => void;
  onDismissBackdrop: () => void;
  onCategoryInput: (value: string) => void;
  onPickCategory: (id: string | null) => void;
  onMemoPatch: (title: string, body: string) => void;
  onMisc: (value: string) => void;
  onCancel: () => void;
  onSave: (payload: TodoRoutineCreatePayload) => void;
};

const TodoRoutineCreateModal: React.FC<Props> = ({
  open,
  draft,
  categories,
  categoryInput,
  saving = false,
  mode = 'create',
  initialRoutine = null,
  occurrenceYmd = null,
  completeToggleMode = 'never',
  completeMark = null,
  onToggleComplete,
  onDelete,
  onDismissBackdrop,
  onCategoryInput,
  onPickCategory,
  onMemoPatch,
  onMisc,
  onCancel,
  onSave,
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [frequency, setFrequency] = useState<TodoRoutineFrequency>('weekly');
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  /** 편집 중 빈 값 허용(백스페이스로 전부 삭제) */
  const [monthDay, setMonthDay] = useState<number | ''>(1);
  const [startDate, setStartDate] = useState(() => todayIso());
  const [endDate, setEndDate] = useState(() => todayIso());
  const [displayUnit, setDisplayUnit] = useState<TodoRoutineDisplayUnit>('week');
  /** 오늘 기준 앞·뒤 동일 기간 */
  const [displaySpan, setDisplaySpan] = useState(2);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const isEdit = mode === 'edit';
  const occYmd = (occurrenceYmd ?? '').trim().slice(0, 10);
  const showHeaderCompleteToggle =
    isEdit &&
    !!onToggleComplete &&
    completeToggleMode === 'always' &&
    /^\d{4}-\d{2}-\d{2}$/.test(occYmd);

  useEffect(() => {
    if (!open) return;
    const today = todayIso();
    if (isEdit && initialRoutine) {
      setFrequency(initialRoutine.frequency);
      setWeekdays(
        initialRoutine.weekdays?.length ? [...initialRoutine.weekdays] : [1, 2, 3, 4, 5]
      );
      setMonthDay(initialRoutine.monthDay ?? 1);
      setStartDate(initialRoutine.startDate?.trim() || today);
      setEndDate(initialRoutine.endDate?.trim() || today);
      setDisplayUnit(initialRoutine.display?.unit ?? 'week');
      const before = initialRoutine.display?.before ?? 2;
      const after = initialRoutine.display?.after ?? before;
      setDisplaySpan(Math.max(before, after));
      return;
    }
    setFrequency('weekly');
    setWeekdays([1, 2, 3, 4, 5]);
    setMonthDay(1);
    setStartDate(today);
    setEndDate(today);
    setDisplayUnit('week');
    setDisplaySpan(2);
    // draft.id / mode 변경 시에만 초기화 (편집 중 initialRoutine 참조 변경으로 리셋되지 않게)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, draft?.id]);

  useEffect(() => {
    if (!open) setDeleteConfirmOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (deleteConfirmOpen) {
        setDeleteConfirmOpen(false);
        return;
      }
      onDismissBackdrop();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onDismissBackdrop, deleteConfirmOpen]);

  const resolvedMonthDay = typeof monthDay === 'number' ? monthDay : 1;

  const draftRoutine = useMemo(
    () =>
      defaultRoutineConfig({
        frequency,
        weekdays: frequency === 'weekly' ? weekdays : undefined,
        monthDay: frequency === 'monthly' ? resolvedMonthDay : undefined,
        startDate: startDate.trim() || null,
        endDate: endDate.trim() || null,
        display: {
          unit: displayUnit,
          before: Math.max(0, displaySpan),
          after: Math.max(0, displaySpan),
        },
      }),
    [
      frequency,
      weekdays,
      resolvedMonthDay,
      startDate,
      endDate,
      displayUnit,
      displaySpan,
    ]
  );

  const occurrenceGapMessage = useMemo(
    () => getRoutineOccurrenceGapMessage(draftRoutine),
    [draftRoutine]
  );

  const canSave = useMemo(() => {
    if (!draft?.due_date) return false;
    if (frequency === 'weekly' && weekdays.length === 0) return false;
    if (occurrenceGapMessage) return false;
    return true;
  }, [draft?.due_date, frequency, weekdays.length, occurrenceGapMessage]);

  if (!open || !draft) return null;

  const toggleWeekday = (d: number) => {
    setWeekdays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)));
  };

  const handleSave = () => {
    if (!canSave) return;
    const gap = getRoutineOccurrenceGapMessage(draftRoutine);
    if (gap) return;
    const routine = defaultRoutineConfig({
      ...draftRoutine,
      dayLogs: isEdit ? normalizeRoutineDayLogs(initialRoutine?.dayLogs) : {},
    });
    onSave({ draft, routine });
  };

  const unitLabel = displayUnit === 'week' ? '주' : '달';
  const titleText = isEdit ? '루틴 과제 수정' : '루틴 과제 추가';
  const saveLabel = isEdit ? '저장' : '추가';

  const modal = (
    <div
      className="cp-todo-note-item-add-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={titleText}
    >
      <div
        className="cp-todo-note-item-add-stack cp-todo-routine-create-stack"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cp-todo-note-item-add-stack-head">
          <div className="cp-todo-note-item-add-header-side is-start">
            {showHeaderCompleteToggle ? (
              <button
                type="button"
                className={`cp-todo-note-item-add-header-complete-row${draft.completed ? ' is-done' : ''}${
                  completeMark === 'x' ? ' is-auto-missed' : ''
                }`}
                aria-pressed={draft.completed}
                aria-label={draft.completed ? '완료 해제' : '완료 체크'}
                onClick={() => onToggleComplete?.()}
              >
                <span
                  className={`cp-todo-note-item-add-header-complete${draft.completed ? ' is-done' : ''}${
                    completeMark === 'x' ? ' is-auto-missed' : ''
                  }`}
                  aria-hidden
                >
                  {draft.completed ? (
                    completeMark === 'x' ? (
                      <svg
                        className="cp-todo-note-complete-mark cp-todo-note-complete-mark--x"
                        viewBox="0 0 20 20"
                        width="14"
                        height="14"
                        aria-hidden
                        focusable="false"
                      >
                        <path
                          d="M5.5 5.5 L14.5 14.5 M14.5 5.5 L5.5 14.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.15"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="cp-todo-note-complete-mark"
                        viewBox="0 0 20 20"
                        width="14"
                        height="14"
                        aria-hidden
                        focusable="false"
                      >
                        <path
                          d="M5.05 10.35 L9.25 14.65 L15.35 5.55"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.15"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )
                  ) : null}
                </span>
                <span className="cp-todo-note-item-add-header-complete-label" aria-hidden>
                  완료 체크
                </span>
              </button>
            ) : null}
          </div>
          <h3 className="cp-todo-note-item-add-stack-title">{titleText}</h3>
          <div className="cp-todo-note-item-add-header-side is-end">
            {isEdit && onDelete ? (
              <button
                type="button"
                className="cp-todo-note-item-add-header-delete"
                disabled={saving}
                title="삭제"
                aria-label="루틴 과제 삭제"
                onClick={() => setDeleteConfirmOpen(true)}
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/Trash.svg`}
                  alt=""
                  width={16}
                  height={16}
                />
              </button>
            ) : null}
          </div>
        </div>

        <div className="cp-todo-routine-settings">
          <div className="cp-todo-routine-settings-row cp-todo-routine-settings-row--freq">
            <div className="cp-todo-routine-aligned-controls">
              <div className="cp-todo-routine-freq-main">
                <div className="cp-todo-routine-freq-row cp-todo-routine-freq-row--repeat" role="group" aria-label="노트 반복">
                  {(
                    [
                      ['daily', '매일'],
                      ['weekly', '매주'],
                      ['monthly', '매월'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`cp-todo-routine-chip${frequency === value ? ' is-on' : ''}`}
                      onClick={() => setFrequency(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {frequency === 'monthly' ? (
            <div className="cp-todo-routine-settings-row cp-todo-routine-settings-row--days">
              <div className="cp-todo-routine-month-day" role="group" aria-label="매월 일자">
                <span>매월</span>
                <input
                  className="cp-todo-routine-num cp-todo-routine-num--month-day"
                  type="number"
                  min={1}
                  max={31}
                  value={monthDay}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setMonthDay('');
                      return;
                    }
                    const n = Number(raw);
                    if (!Number.isFinite(n)) return;
                    setMonthDay(Math.min(31, Math.max(1, Math.trunc(n))));
                  }}
                  onBlur={() => {
                    if (monthDay === '' || monthDay < 1) setMonthDay(1);
                  }}
                />
                <span>일</span>
                <div className="cp-apn-create-inc">
                  <button
                    type="button"
                    className="cp-apn-create-inc-btn"
                    aria-label="1일로 설정"
                    title="1일로 설정"
                    onClick={() => setMonthDay(1)}
                  >
                    <IconReset />
                  </button>
                  <button
                    type="button"
                    className="cp-apn-create-inc-btn"
                    aria-label="일자 빼기"
                    onClick={() =>
                      setMonthDay((n) => Math.max(1, (typeof n === 'number' ? n : 1) - 1))
                    }
                  >
                    <IconMinus />
                  </button>
                  <button
                    type="button"
                    className="cp-apn-create-inc-btn"
                    aria-label="일자 더하기"
                    onClick={() =>
                      setMonthDay((n) => Math.min(31, (typeof n === 'number' ? n : 1) + 1))
                    }
                  >
                    <IconPlus />
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {frequency === 'weekly' ? (
            <div className="cp-todo-routine-settings-row cp-todo-routine-settings-row--days">
              <span className="cp-todo-routine-settings-label">요일</span>
              <div className="cp-todo-routine-days" role="group" aria-label="요일">
                {WEEKDAY_OPTIONS.map((d) => {
                  const on = weekdays.includes(d.v);
                  return (
                    <button
                      key={d.v}
                      type="button"
                      className={`cp-todo-routine-day${on ? ' is-on' : ''}`}
                      onClick={() => toggleWeekday(d.v)}
                      aria-pressed={on}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="cp-todo-routine-settings-row cp-todo-routine-settings-row--period">
            <span className="cp-todo-routine-settings-label cp-todo-routine-settings-label--wide">
              노트 반복 시작 기간
            </span>
            <div className="cp-todo-routine-period-field">
              <CreateDateField
                label="노트 반복 시작 기간"
                value={startDate}
                onChange={setStartDate}
                inline
                hideLabel
              />
            </div>
          </div>

          <div className="cp-todo-routine-settings-row cp-todo-routine-settings-row--period">
            <span className="cp-todo-routine-settings-label cp-todo-routine-settings-label--wide">
              노트 반복 마감 기간
            </span>
            <div className="cp-todo-routine-period-field">
              <CreateDateField
                label="노트 반복 마감 기간"
                value={endDate}
                onChange={setEndDate}
                inline
                hideLabel
              />
            </div>
          </div>

          <div className="cp-todo-routine-settings-row cp-todo-routine-settings-row--display">
            <span className="cp-todo-routine-settings-label cp-todo-routine-settings-label--wide">
              노트 표시 기간
            </span>
            <div className="cp-todo-routine-aligned-controls">
              <div className="cp-todo-routine-freq-main">
                <div
                  className="cp-todo-routine-freq-row cp-todo-routine-freq-row--unit"
                  role="group"
                  aria-label="표시 단위"
                >
                  <button
                    type="button"
                    className={`cp-todo-routine-chip${displayUnit === 'week' ? ' is-on' : ''}`}
                    onClick={() => setDisplayUnit('week')}
                  >
                    주
                  </button>
                  <button
                    type="button"
                    className={`cp-todo-routine-chip${displayUnit === 'month' ? ' is-on' : ''}`}
                    onClick={() => setDisplayUnit('month')}
                  >
                    달
                  </button>
                </div>
                <div className="cp-todo-routine-settings-extra">
                  <span className="cp-todo-routine-settings-label">앞뒤</span>
                  <input
                    className="cp-todo-routine-num cp-todo-routine-num--span"
                    type="number"
                    min={0}
                    max={52}
                    value={displaySpan}
                    onChange={(e) => setDisplaySpan(Math.max(0, Number(e.target.value) || 0))}
                    aria-label="표시 앞뒤 기간"
                  />
                  <span className="cp-todo-routine-unit-suffix">{unitLabel}</span>
                </div>
              </div>
              <div className="cp-apn-create-inc">
                <button
                  type="button"
                  className="cp-apn-create-inc-btn"
                  aria-label="표시 기간 초기화"
                  onClick={() => setDisplaySpan(2)}
                >
                  <IconReset />
                </button>
                <button
                  type="button"
                  className="cp-apn-create-inc-btn"
                  aria-label="표시 기간 줄이기"
                  onClick={() => setDisplaySpan((n) => Math.max(0, n - 1))}
                >
                  <IconMinus />
                </button>
                <button
                  type="button"
                  className="cp-apn-create-inc-btn"
                  aria-label="표시 기간 늘리기"
                  onClick={() => setDisplaySpan((n) => Math.min(52, n + 1))}
                >
                  <IconPlus />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="cp-todo-note-item-add-block">
          <div className="cp-todo-note-item-add-modal-inner">
            <DndContext sensors={sensors}>
              <SortableContext items={[draft.id]} strategy={verticalListSortingStrategy}>
                <SortableRow
                  item={draft}
                  ord={0}
                  categories={categories}
                  categoryInput={categoryInput}
                  onCategoryInput={onCategoryInput}
                  onPickCategory={onPickCategory}
                  onDueDate={() => {}}
                  onDueSwitchToMonthOnly={() => {}}
                  onDueSwitchToDay={() => {}}
                  onMemoPatch={onMemoPatch}
                  onMisc={onMisc}
                  onToggleComplete={
                    showHeaderCompleteToggle ? () => onToggleComplete?.() : () => {}
                  }
                  onRemove={() => {}}
                  dragDisabled
                  selected={false}
                  onSelect={() => {}}
                  editorMode
                  autoFocusMemo
                  routineBadge
                  completeMark={showHeaderCompleteToggle ? completeMark : null}
                />
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {occurrenceGapMessage ? (
          <p className="cp-todo-routine-occurrence-gap" role="status">
            {occurrenceGapMessage}
          </p>
        ) : null}

        <div className="cp-todo-note-item-add-actions">
          <button type="button" className="cp-btn-secondary" onClick={onCancel} disabled={saving}>
            취소
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            onClick={handleSave}
            disabled={saving || !canSave}
            title={occurrenceGapMessage || undefined}
          >
            {saveLabel}
          </button>
        </div>
      </div>
      {deleteConfirmOpen ? (
        <div
          className="cp-todo-note-item-add-discard-dialog cp-todo-note-item-add-delete-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-todo-routine-delete-title"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <h3 id="cp-todo-routine-delete-title">루틴 과제를 삭제할까요?</h3>
          <p className="cp-todo-note-item-add-discard-desc">
            이 루틴 블럭을 삭제하면 모든 날짜의 루틴 블럭 기록이 함께 삭제됩니다.
          </p>
          <div className="cp-todo-note-item-add-discard-actions">
            <button type="button" className="cp-btn-secondary" onClick={() => setDeleteConfirmOpen(false)}>
              취소
            </button>
            <button
              type="button"
              className="cp-btn-primary"
              onClick={() => {
                setDeleteConfirmOpen(false);
                onDelete?.();
              }}
              disabled={saving}
            >
              삭제
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  return createPortal(modal, document.body);
};

export default TodoRoutineCreateModal;
